import { Tooltip } from '@material-ui/core';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useGesture } from 'react-with-gesture';
import { mapIsoStringtoUtcObject } from '~/modules/common/dates/convert';
import { useOnRequestDateRangeChangeHandler } from '~/modules/projects/resourcing-plan/enhancers/withScheduleRulesBasedUpdateHandlers';
import {
  DragEndIndicator,
  DragStartIndicator
} from '~/modules/resourcing/common/chart/components';
import PeriodTimelineBlocks from '~/modules/resourcing/common/chart/components/PeriodTimelineBlocks';
import useRequestTimelineBlocks from '~/modules/resourcing/common/chart/hooks/useRequestTimelineBlocks';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import { getStatusBasedClassName } from '~/modules/resourcing/common/enums';
import {
  getEntityDateRange,
  isInChartDisplayDateRange
} from '~/modules/resourcing/common/hooks/useClippingDateRangeUtil';
import useEstimatedResourceRequest from './hooks/useEstimatedResourceRequest';
import useEstimatedResourceRequestDateChangeEffect from './hooks/useEstimatedResourceRequestDateChangeEffect';
import ResourceRequestTooltipContent from './ResourceRequestTooltipContent/ResourceRequestTooltipContent';
import useStyles from './useStyles';

export const ResourceRequestEditableBlock = ({
  canViewCostData,
  chartDisplayDateRange,
  chartStartDate,
  displayUnit,
  isAllocationBlockPresent = false,
  qeIdTag,
  resourceRequest,
  scale,
  isPSAActualCornerCasesEnabled,
  isResourceActualModeEnabled
}) => {
  const classes = useStyles();

  const dragIndicatorClasses = useMemo(
    () => ({
      resizeHandle: classes.resizeHandle,
      resizeHandleStart: classes.resizeHandleStart,
      resizeHandleEnd: classes.resizeHandleEnd,
      resizeIcon: classes.resizeIcon
    }),
    [
      classes.resizeHandle,
      classes.resizeHandleStart,
      classes.resizeHandleEnd,
      classes.resizeIcon
    ]
  );

  const requestDateRange = getEntityDateRange(resourceRequest);

  const isStartChartBarClipped =
    requestDateRange.startDate < chartDisplayDateRange.startDate;
  const isEndChartBarClipped =
    requestDateRange.endDate > chartDisplayDateRange.endDate;

  const isInChartRange = isInChartDisplayDateRange({
    chartDisplayDateRange,
    entityDateRange: requestDateRange
  });

  const { project } = useProjectContext();
  const [, { target, delta, down }] = useGesture();
  const {
    dynamicStyle,
    estimatedResourceRequest
  } = useEstimatedResourceRequest({
    chartStartDate,
    delta,
    down,
    project,
    resourceRequest,
    scale,
    target
  });

  const onRequestDateRangeChange = useOnRequestDateRangeChangeHandler({
    project,
    resourceRequest,
    isPSAActualCornerCasesEnabled,
    isResourceActualModeEnabled
  });

  useEstimatedResourceRequestDateChangeEffect({
    down,
    estimatedResourceRequest,
    onRequestDateRangeChange
  });

  const request = useMemo(
    () => ({
      ...resourceRequest,
      ...estimatedResourceRequest
    }),
    [resourceRequest, estimatedResourceRequest]
  );
  const title = useMemo(
    () => (
      <ResourceRequestTooltipContent
        resourceRequest={request}
        canViewCostData={canViewCostData}
      />
    ),
    [request, canViewCostData]
  );

  const { requestPeriods } = useRequestTimelineBlocks({
    scheduleRules: resourceRequest.scheduleRules,
    chartStartDate,
    scale,
    startDate: mapIsoStringtoUtcObject(resourceRequest.startDate),
    endDate: mapIsoStringtoUtcObject(resourceRequest.endDate),
    relativeDynamicPositionLeft: dynamicStyle.left
  });

  return (
    isInChartRange && (
      <div
        className={classNames(classes.root, {
          [classes.disablePointerEvents]: isAllocationBlockPresent
        })}
        style={dynamicStyle}
      >
        <Tooltip title={title}>
          <div
            className={classNames(
              classes.container,
              classes.editableContainer,
              classes[
                getStatusBasedClassName(
                  resourceRequest.requestStatus,
                  'container'
                )
              ],
              {
                [classes.containerNoStart]: isStartChartBarClipped,
                [classes.containerNoEnd]: isEndChartBarClipped
              }
            )}
          >
            {!isAllocationBlockPresent && (
              <PeriodTimelineBlocks
                scale={scale}
                quantity={resourceRequest.quantity}
                displayPeriods={requestPeriods}
                displayUnit={displayUnit}
                qeIdTag={qeIdTag}
              />
            )}

            {!isStartChartBarClipped && (
              <DragStartIndicator classes={dragIndicatorClasses} />
            )}

            {!isEndChartBarClipped && (
              <DragEndIndicator classes={dragIndicatorClasses} />
            )}
          </div>
        </Tooltip>
      </div>
    )
  );
};

ResourceRequestEditableBlock.propTypes = {
  qeIdTag: PropTypes.string,
  scale: PropTypes.string.isRequired,
  resourceRequest: PropTypes.object.isRequired,
  canViewCostData: PropTypes.bool,
  isAllocationBlockPresent: PropTypes.bool,
  chartStartDate: PropTypes.object,
  displayUnit: PropTypes.string,
  chartDisplayDateRange: PropTypes.object,
  isPSAActualCornerCasesEnabled: PropTypes.bool,
  isResourceActualModeEnabled: PropTypes.bool
};

export default ResourceRequestEditableBlock;
